import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Tile, TileContent, TileStateIndicator, TileText } from '@one-thd/sui-atomic-components';
import PropTypes from 'prop-types';
import { SquirclePill } from '../SquirclePill/SquirclePill';
import { deliveryOptionPropType } from '../../utils/deliveryUtils';
import { DELIVERY_REFINEMENTS } from '../../utils/constants';

const {
  arrayOf, bool, func, oneOf, oneOfType, shape
} = PropTypes;

const DeliveryOptions = ({
  options,
  onChange,
  skipAutoSelect,
  triggerDeliverySpeedChange,
  setTriggerDeliverySpeedChange,
  deselect
}) => {
  const syncSelectedOption = options?.find((option) => option?.isSelected);
  const getSelectedOptionInitial = () => {
    if (skipAutoSelect) {
      return '';
    }
    return syncSelectedOption ? syncSelectedOption?.label : options[0]?.label || '';
  };
  const [selectedOption, setSelectedOption] = useState(getSelectedOptionInitial());

  const handleDeliverySpeedChange = (option) => {
    const newOptionValue = option?.value || option;

    if (selectedOption !== newOptionValue) {
      const normalizedOption = newOptionValue || '';
      setSelectedOption(normalizedOption);

      if (!option?.localOnly) {
        onChange(normalizedOption);
      }
    }
  };

  useEffect(() => {
    if (syncSelectedOption) {
      setSelectedOption(syncSelectedOption?.label);
    }
  }, [syncSelectedOption]);

  useEffect(() => {
    if (deselect && skipAutoSelect) {
      setSelectedOption('');
    }
    if (triggerDeliverySpeedChange !== null) {
      handleDeliverySpeedChange(triggerDeliverySpeedChange);
      // Delay resetting triggerDeliverySpeedChange to ensure it is processed
      setTimeout(() => {
        if (setTriggerDeliverySpeedChange) {
          setTriggerDeliverySpeedChange(null);
        }
      }, 0);
    }
  }, [
    triggerDeliverySpeedChange,
    setTriggerDeliverySpeedChange,
    deselect,
    skipAutoSelect
  ]);

  return (
    <div className="sui-flex sui-gap-3 sui-items-center sui-overflow-x-auto" data-testid="delivery-options">
      {options.map((option) => {
        const isSelected = selectedOption === option.label;

        return !option.isHidden && (
          <SquirclePill
            key={option.label}
            isSelected={isSelected}
            isUnavailable={option.isDisabled}
            className={classNames(
              isSelected && '[&>button]:sui-border-strongest'
            )}
          >
            <Tile
              onClick={option.isDisabled ? null : () => handleDeliverySpeedChange(option.label)}
              unavailable={option.isDisabled}
              selected={isSelected}
              variant="outline"
              data-unavailable={option.isDisabled}
            >
              <TileContent orientation="horizontal">
                <TileText component="div" data-testid={`emt-link-${option.label}`}>
                  {/* SEO-friendly hidden link - Required for SEO significance.
                    Reach out to SEO team if wanting to change */}
                  <a
                    href={option.url}
                    className="sui-hidden"
                    aria-hidden="true"
                    tabIndex="-1"
                  >
                    {/* H3 element required for SEO significance. Reach out to SEO team if wanting to change */}
                    <h3>
                      {option.label}
                    </h3>
                  </a>
                  {option.label}
                </TileText>
                {isSelected && <TileStateIndicator />}
              </TileContent>
            </Tile>
          </SquirclePill>
        );
      })}
    </div>
  );
};

DeliveryOptions.propTypes = {
  onChange: func.isRequired,
  options: arrayOf(deliveryOptionPropType).isRequired,
  skipAutoSelect: bool,
  triggerDeliverySpeedChange: shape({
    value: oneOf([
      DELIVERY_REFINEMENTS.ANYTIME,
      DELIVERY_REFINEMENTS.TODAY,
      DELIVERY_REFINEMENTS.BY_TOMORROW
    ]),
    localOnly: bool
  }),
  setTriggerDeliverySpeedChange: oneOfType([
    func,
    oneOf([null])
  ]),
  deselect: bool
};

DeliveryOptions.defaultProps = {
  skipAutoSelect: false,
  triggerDeliverySpeedChange: null,
  setTriggerDeliverySpeedChange: null,
  deselect: false
};

export { DeliveryOptions };